import React, { ReactNode, useEffect } from "react";
import { useDialogPolyfill } from "@wss/dialog";
import { cls } from "cls";
import {
  controlClassNames,
  dialogClassNames,
  dialogAnimationClassNames,
  headerClassNames,
  headingContainerClassNames,
  headingClassNames,
  spacerClassNames,
} from "./drawerModal.styles";

if (window.HTMLDialogElement === undefined)
  import("dialog-polyfill/dialog-polyfill.css");

interface Props {
  id: string;
  dialogRef: React.RefObject<HTMLDialogElement>;
  children: JSX.Element;
  title: string;
  subtitle?: string;
  leftControlContent?: React.ReactNode;
  leftControlFunction?: () => void;
  leftControlHref?: string;
  rightControlContent?: React.ReactNode;
  rightControlFunction?: () => void;
  rightControlHref?: string;
  dialogClasses?: string;
  dialogAnimationClasses?: string;
  controlClasses?: string;
  headerClasses?: string;
  headerStyles?: React.CSSProperties;
  headingClasses?: string;
  subtitleClasses?: string;
  spacerClasses?: string;
  onClose?: () => void;
  leftControlArialLabelText?: string;
}

const DrawerModal = (props: Props) => {
  const {
    id,
    dialogRef,
    children,
    title,
    subtitle,
    leftControlContent,
    leftControlFunction,
    leftControlHref,
    rightControlContent,
    rightControlFunction,
    rightControlHref,
    dialogClasses,
    dialogAnimationClasses,
    controlClasses,
    headerClasses,
    headerStyles = {},
    headingClasses = headingClassNames,
    subtitleClasses,
    spacerClasses,
    onClose,
    leftControlArialLabelText = "",
  } = props;

  const headingId = `dialogHeading${title.replace(/\s/g, "")}`;

  useDialogPolyfill(dialogRef);

  useEffect(() => {
    const dialogReference = dialogRef;

    if (dialogReference.current && onClose) {
      dialogReference.current?.addEventListener("close", onClose);
    }

    return () => {
      if (dialogReference.current && onClose) {
        dialogReference.current?.removeEventListener("close", onClose);
      }
    };
  }, [dialogRef, onClose]);

  const renderControl = (
    contentConditional: ReactNode | undefined,
    hrefConditional: string | undefined,
    isLeftSide: boolean,
  ) => {
    if (contentConditional !== undefined && hrefConditional !== undefined) {
      return (
        <a
          href={isLeftSide ? leftControlHref : rightControlHref}
          className={cls(
            controlClassNames,
            isLeftSide ? "align-left" : "align-right",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-6px]",
            controlClasses || "",
          )}
          onClick={
            isLeftSide
              ? leftControlFunction && leftControlFunction
              : rightControlFunction && rightControlFunction
          }
        >
          {isLeftSide ? leftControlContent : rightControlContent}
        </a>
      );
    }
    if (contentConditional !== undefined) {
      return (
        <button
          {...(leftControlArialLabelText && {
            "aria-label": leftControlArialLabelText,
          })}
          type="button"
          className={cls(
            controlClassNames,
            isLeftSide ? "text-left" : "text-right",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-6px]",
            controlClasses || "",
          )}
          onClick={isLeftSide ? leftControlFunction : rightControlFunction}
        >
          {isLeftSide ? leftControlContent : rightControlContent}
        </button>
      );
    }

    return <span className={cls(spacerClassNames, spacerClasses || "")} />;
  };

  return (
    <dialog
      className={cls(
        dialogClassNames,
        dialogClasses || "",
        dialogAnimationClasses || dialogAnimationClassNames,
      )}
      id={id}
      ref={dialogRef}
      data-testid="drawerModalDialog"
      aria-labelledby={headingId}
    >
      <header
        className={cls(headerClassNames, headerClasses || "")}
        style={headerStyles}
      >
        {renderControl(leftControlContent, leftControlHref, true)}
        <div className={headingContainerClassNames}>
          <h2 id={headingId} className={cls(headingClasses)}>
            {title}
          </h2>
          {subtitle && <p className={subtitleClasses}>{subtitle}</p>}
        </div>
        {renderControl(rightControlContent, rightControlHref, false)}
      </header>
      {children}
    </dialog>
  );
};

export default DrawerModal;
